import { useEffect, useRef } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { useLazyQuery } from "@apollo/client";
import SessionFields from "../components/SessionFields";
import MaxAttendanceField from "../components/MaxAttendanceField";
import Button from "@/components/Button";
import Radio from "@/components/Radio";
import Divider from "@/components/Divider";
import {
  buildSeminarSessions,
  getGlobalMaxAttendance,
  getDefaultRegistrationClosingDate,
  getDefaultSession,
  getDisplayTimezone,
  getRecruitmentRegistrationDates,
  isSeminarFormEvent,
  mapFeeDefaultsToEventFees
} from "../utils";
import { GET_EVENT_DEFAULTS } from "@/pages/events/queries";
import type { EventDefaultsQueryData } from "../types";
import { DateTime } from "luxon";
import { useSetDefaultsCalled } from "../FormVisibilityContext";

const REGISTRATION_TYPES = [
  { value: "individual", label: "Registrant can select individual sessions" },
  { value: "all", label: "Registration includes ALL sessions" }
];

const SessionDetailsStep = ({
  isEditing,
  originalRegistrationCloseDate
}: {
  isEditing?: boolean;
  originalRegistrationCloseDate?: any;
}) => {
  const { control, watch, setValue } = useFormContext();
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "sessions"
  });

  const hasMultipleSessions = fields.length > 1;

  const sessions = watch("sessions");
  const registrationType = watch("registrationType");
  const globalMaxAttendance = watch("globalMaxAttendance");
  const timezone = watch("timezone");
  const eventType = watch("eventType");
  const orderTemplateId = watch("orderTemplateId");
  const eventFees = watch("eventFees");
  const isSeminar = isSeminarFormEvent({ eventType, orderTemplateId, eventFees });
  const firstSessionDate = sessions?.[0]?.date?.startDate;
  const setDefaultsCalled = useSetDefaultsCalled();

  const [fetchEventDefaults] = useLazyQuery<EventDefaultsQueryData>(GET_EVENT_DEFAULTS, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const defaults = data?.eventDefaults;
      if (!defaults) return;
      setValue("eventFees", mapFeeDefaultsToEventFees(defaults.feeDefaults ?? []));
      if (!defaults.sessions) return;
      setDefaultsCalled(true);
      const seminarSessions = buildSeminarSessions(defaults.sessions);
      setValue("orderTemplateId", defaults.orderTemplateId ?? "");
      setValue("globalMaxAttendance", getGlobalMaxAttendance(seminarSessions));
      replace(seminarSessions);
      const registrationCloseDate = DateTime.fromISO(defaults.sessions?.[0]?.startsAt)
        .minus({ days: defaults.registrationClosesDaysBefore })
        .toFormat("yyyy-MM-dd");
      setValue("registrationCloseDate", { startDate: registrationCloseDate, endDate: registrationCloseDate });
    }
  });

  const prevFirstSessionDate = useRef<string | undefined>(undefined);
  const prevActivityTypeId = useRef<string | undefined>(undefined);
  const prevSessionCount = useRef(sessions?.length ?? fields.length);

  useEffect(() => {
    if (!isSeminar || !firstSessionDate || !eventType?.id) return;
    if (isEditing) return;

    const sessionDateChanged = firstSessionDate !== prevFirstSessionDate.current;
    const activityTypeChanged = eventType.id !== prevActivityTypeId.current;
    if (!sessionDateChanged && !activityTypeChanged) return;

    prevFirstSessionDate.current = firstSessionDate;
    prevActivityTypeId.current = eventType.id;

    fetchEventDefaults({
      variables: {
        activityTypeId: eventType.id,
        sessionStartDate: firstSessionDate
      }
    });
  }, [isEditing, isSeminar, firstSessionDate, fetchEventDefaults, eventType?.id]);

  useEffect(() => {
    if (eventType.name !== "Recruitment" || !firstSessionDate || isEditing) return;

    const dates = getRecruitmentRegistrationDates(sessions);
    if (!dates) return;

    setValue("registrationOpenDate", dates.registrationOpenDate);
    setValue("registrationCloseDate", dates.registrationCloseDate);
  }, [eventType.name, firstSessionDate, sessions, setValue, isEditing]);

  useEffect(() => {
    const sessionCount = sessions?.length ?? 0;
    const previousSessionCount = prevSessionCount.current;
    prevSessionCount.current = sessionCount;

    if (sessionCount !== 1) return;

    const sessionMaxAttendance = sessions?.[0]?.maxAttendance ?? "";
    const globalMaxAttendanceValue = globalMaxAttendance ?? "";

    if (registrationType === "all" && previousSessionCount > 1 && sessionMaxAttendance !== globalMaxAttendanceValue) {
      setValue("sessions.0.maxAttendance", globalMaxAttendanceValue);
      return;
    }

    if (globalMaxAttendanceValue !== sessionMaxAttendance) {
      setValue("globalMaxAttendance", sessionMaxAttendance);
    }
  }, [globalMaxAttendance, registrationType, sessions, setValue]);

  useEffect(() => {
    if ((eventType.name === "PAT" || eventType.name === "Recruitment") && fields.length > 1) {
      setValue("sessions", fields.slice(0, 1));
    }
  }, [eventType, fields, setValue]);

  const addSession = () => {
    if (registrationType === "all" && sessions.length === 1) {
      setValue("globalMaxAttendance", sessions[0]?.maxAttendance ?? "");
    }

    if (isSeminar && sessions.length === 0) {
      append(buildSeminarSessions([{ startsAt: "", endsAt: "", maxAttendance: 0 }]));
      return;
    }
    const prevSession = sessions[sessions.length - 1];
    const newSessionDefaults = getDefaultSession(prevSession);
    append(newSessionDefaults);
  };

  const removeSession = (index: number) => {
    if (registrationType === "all" && sessions.length === 2) {
      const remainingIndex = index === 0 ? 1 : 0;
      setValue(`sessions.${remainingIndex}.maxAttendance`, globalMaxAttendance ?? "");
    }

    remove(index);
  };

  const handleSessionChange = (nextSessions?: typeof sessions) => {
    if (eventType.name === "Recruitment") {
      const dates = getRecruitmentRegistrationDates(nextSessions ?? sessions);
      if (dates) {
        setValue("registrationOpenDate", dates.registrationOpenDate);
        setValue("registrationCloseDate", dates.registrationCloseDate);
      }
      return;
    }

    if (isSeminar || isEditing) return;
    setValue(
      "registrationCloseDate",
      getDefaultRegistrationClosingDate(
        nextSessions ?? sessions,
        eventType.name,
        isEditing,
        originalRegistrationCloseDate
      )
    );
  };

  return (
    <div>
      <div className="flex flex-col gap-6 mb-6">
        <p className="text-title-large mb-2"> When will this event be hosted?</p>
        {eventType.name !== "PAT" && eventType.name !== "Recruitment" && (
          <p className="text-body-medium">
            Note: If session dates and/or times are changed after registrations have been received, any applicable
            cancellation or refund restrictions will be waived. Registrants will be notified about any session changes.
          </p>
        )}
        <div className="flex flex-col gap-6">
          {fields.map((field, index) => (
            <SessionFields
              key={field.id}
              index={index}
              handleSessionChange={handleSessionChange}
              timezone={getDisplayTimezone(timezone)}
              hasMultipleSessions={hasMultipleSessions}
              eventType={eventType}
              maxAttendanceDisabled={registrationType === "all" && hasMultipleSessions}
              maxAttendanceDisplayValue={
                registrationType === "all" && hasMultipleSessions ? globalMaxAttendance : undefined
              }
              onRemove={() => {
                if (hasMultipleSessions) {
                  removeSession(index);
                }
              }}
            />
          ))}
        </div>
        {eventType.name !== "PAT" && eventType.name !== "Recruitment" && (
          <div>
            <Button variant="filledTonal" icon="add" theme="info" onClick={addSession}>
              Add Session
            </Button>
          </div>
        )}
      </div>

      {eventType.name !== "PAT" && eventType.name !== "Recruitment" && (
        <>
          <div className="py-3">
            <Divider />
          </div>
          {hasMultipleSessions && (
            <div className="mt-6">
              <p className="text-title-medium mb-2">Multiple Sessions</p>
              <p className="text-body-medium mb-3">
                Select session registration handling for a single event that is being held over multiple dates/times.
              </p>
              <Controller
                control={control}
                name="registrationType"
                data-testid="registration-type-radio-group"
                render={({ field }) => (
                  <div className="flex flex-col">
                    {REGISTRATION_TYPES.map((option, i) => (
                      <div className="py-3" key={i}>
                        <Radio
                          name={`registrationType-${option.value}`}
                          label={option.label}
                          checked={field.value === option.value}
                          onChange={() => {
                            field.onChange(option.value);
                            if (option.value === "all") {
                              setValue("globalMaxAttendance", getGlobalMaxAttendance(sessions ?? []));
                            }
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
              />
              {registrationType === "all" && (
                <div className="w-[280px] h-[56px] my-6">
                  <MaxAttendanceField name="globalMaxAttendance" label="Global Max Registrants Allowed" />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SessionDetailsStep;
